import React, { useState, useEffect, useRef } from 'react';
import { X, ThermometerSun, ThermometerSnowflake, Linkedin, Facebook, MessageCircle, Twitter, PlusCircle } from 'lucide-react';
import { Contact } from '../../types';
import GroupInput from '../groups/GroupInput';
import { analytics } from '../../services/analytics';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (contact: Omit<Contact, 'id'>) => void;
  existingGroups: string[];
}

const SUBMISSION_COOLDOWN = 2000; // 2 seconds cooldown

const AddContactModal: React.FC<AddContactModalProps> = ({ 
  isOpen, 
  onClose, 
  onAdd,
  existingGroups 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    selectedGroups: [] as string[],
    notes: '',
    birthday: '',
    warmth: 'warm' as 'warm' | 'cold',
    // Adding social media fields
    linkedin: '',
    facebook: '',
    telegram: '',
    xcom: ''
  });
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const lastSubmitTimeRef = useRef<number>(0);

  // Reset form state when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      // Reset form when opened
      setFormData({
        name: '',
        email: '',
        phone: '',
        selectedGroups: [],
        notes: '',
        birthday: '',
        warmth: 'warm',
        linkedin: '',
        facebook: '',
        telegram: '',
        xcom: ''
      });
      setError(null);
      setIsSubmitting(false);
      setShowSocialMedia(false);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Contact name is required');
      return;
    }
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < SUBMISSION_COOLDOWN) {
      setError(`Please wait before adding another contact`);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    lastSubmitTimeRef.current = now;
    try {
      const contactData = {
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        notes: formData.notes || "",
        ...(formData.birthday ? { birthday: formData.birthday } : {}),
        warmth: formData.warmth,
        lastInteraction: new Date().toISOString(),
        groups: formData.selectedGroups || [],
        interactions: [],
        todos: [],
        linkedin: formData.linkedin.trim() || undefined,
        facebook: formData.facebook.trim() || undefined,
        telegram: formData.telegram.trim() || undefined,
        xcom: formData.xcom.trim() || undefined
      };
      onAdd(contactData);
      analytics.trackEvent('add_contact', {
        warmth: formData.warmth,
        has_email: !!formData.email,
        has_groups: formData.selectedGroups.length > 0,
        groups_count: formData.selectedGroups.length
      });
      onClose(); // Close after successful add
    } catch (error) {
      console.error('Error adding contact:', error);
      setError('Failed to add contact. Please try again.');
      setIsSubmitting(false); // Ensure submitting state is reset on error
    } // No finally block needed here as onClose handles success
  };

  if (!isOpen) return null;

  return (
    // Overlay Style
    <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out">
      {/* Modal Panel Style */}
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        {/* Header Style */}
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Add New Contact</h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="add-contact-form" className="space-y-5">
            {/* Input Styling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.birthday}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Groups</label>
              <GroupInput
                selectedGroups={formData.selectedGroups}
                existingGroups={existingGroups}
                onChange={(groups) => setFormData({ ...formData, selectedGroups: groups })}
              />
            </div>

            {/* Warmth Button Styling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warmth Level</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, warmth: 'warm' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 border ${
                    formData.warmth === 'warm'
                      ? 'bg-tag-yellow-bg text-tag-yellow-text border-yellow-300 shadow-sm' // Active warmth
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive warmth
                  }`}
                  disabled={isSubmitting}
                >
                  <ThermometerSun size={16} /> Warm
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, warmth: 'cold' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 border ${
                    formData.warmth === 'cold'
                      ? 'bg-tag-blue-bg text-tag-blue-text border-blue-300 shadow-sm' // Active warmth
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive warmth
                  }`}
                  disabled={isSubmitting}
                >
                  <ThermometerSnowflake size={16} /> Cold
                </button>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowSocialMedia(!showSocialMedia)}
                className="text-sm text-brand-primary hover:text-indigo-700 flex items-center gap-1"
              >
                 <PlusCircle size={16}/> {showSocialMedia ? 'Hide' : 'Add'} Social Media Profiles
              </button>
              {showSocialMedia && (
                <div className="mt-3 space-y-3 border-t pt-4 border-gray-100">
                  {/* LinkedIn */}
                  <div className="flex items-center gap-2">
                    <Linkedin size={18} className="text-gray-400"/>
                    <input
                      type="url"
                      placeholder="LinkedIn Profile URL"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* Facebook */}
                  <div className="flex items-center gap-2">
                    <Facebook size={18} className="text-gray-400"/>
                    <input
                      type="url"
                      placeholder="Facebook Profile URL"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.facebook}
                      onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* Telegram */}
                  <div className="flex items-center gap-2">
                    <MessageCircle size={18} className="text-gray-400"/>
                    <input
                      type="text" // Telegram is often a username
                      placeholder="Telegram Username or Link"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.telegram}
                      onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* X/Twitter */}
                  <div className="flex items-center gap-2">
                     <Twitter size={18} className="text-gray-400"/>
                     <input
                       type="url"
                       placeholder="X (Twitter) Profile URL"
                       className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                       value={formData.xcom}
                       onChange={(e) => setFormData({ ...formData, xcom: e.target.value })}
                       disabled={isSubmitting}
                     />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                placeholder="Add any relevant notes here..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Placeholder for form actions, actual buttons are in the footer */}
          </form>
        </div>

        {/* Footer Style */}
        <div className="flex justify-end items-center gap-3 p-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          {/* Cancel Button: Secondary Style */}
          <button
            type="button"
            onClick={onClose}
            className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          {/* Add Contact Button: Primary Style */}
          <button
            type="submit" 
            form="add-contact-form" // Link button to the form
            className={`bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal;